import { EventEmitter } from 'events';
import { TwitchatDataTypes } from '@/types/TwitchatDataTypes';
import Utils from '@/utils/Utils';

/**
 * Server-mode Chat Service
 * 
 * WebSocket-based chat service that communicates with the backend TMI.js service.
 * Implements the same interface as the browser TwitchMessengerClient for seamless integration.
 */
export default class ServerChatService extends EventEmitter {
    private static _instance: ServerChatService;
    private _websocket: WebSocket | null = null;
    private _isConnected = false;
    private _isConnecting = false;
    private _messageQueue: any[] = [];
    private _reconnectAttempts = 0;
    private _maxReconnectAttempts = 5;
    private _reconnectDelay = 3000;
    private _heartbeatInterval: number | null = null;
    private _connectedChannels = new Set<string>();

    // Message handlers
    private _messageHandlers = new Map<string, (data: any) => void>();

    private constructor() {
        super();
        this._setupMessageHandlers();
    }

    /**
     * Singleton instance getter
     */
    static get instance(): ServerChatService {
        if (!ServerChatService._instance) {
            ServerChatService._instance = new ServerChatService();
        }
        return ServerChatService._instance;
    }

    /**
     * Connect to backend WebSocket server
     */
    async connect(): Promise<void> {
        if (this._isConnected || this._isConnecting) {
            console.warn('ServerChatService: Already connected or connecting');
            return;
        }

        this._isConnecting = true;

        try {
            const wsUrl = this._getWebSocketUrl();
            console.info(`ServerChatService: Connecting to ${wsUrl}`);

            this._websocket = new WebSocket(wsUrl);
            this._setupWebSocketHandlers();

            // Wait for connection to be established
            await new Promise<void>((resolve, reject) => {
                const timeout = setTimeout(() => {
                    reject(new Error('WebSocket connection timeout'));
                }, 10000);

                this._websocket!.onopen = () => {
                    clearTimeout(timeout);
                    this._isConnected = true;
                    this._isConnecting = false;
                    this._reconnectAttempts = 0;
                    this._startHeartbeat();
                    this._processMessageQueue();
                    console.info('ServerChatService: Connected to backend');
                    resolve();
                };

                this._websocket!.onerror = (error) => {
                    clearTimeout(timeout);
                    this._isConnecting = false;
                    console.error('ServerChatService: Connection error:', error);
                    reject(error);
                };
            });

        } catch (error) {
            this._isConnecting = false;
            console.error('ServerChatService: Failed to connect:', error);
            await this._handleReconnection();
            throw error;
        }
    }

    /**
     * Disconnect from backend
     */
    async disconnect(): Promise<void> {
        if (!this._websocket || !this._isConnected) {
            return;
        }

        this._stopHeartbeat();
        this._websocket.close();
        this._isConnected = false;
        this._connectedChannels.clear();
        console.info('ServerChatService: Disconnected from backend');
    }

    /**
     * Connect to Twitch chat with credentials
     */
    async connectToChat(credentials: { 
        username: string; 
        accessToken: string; 
        pinnedChannels?: { id: string; platform: string; login: string }[];
    }): Promise<void> {
        if (!this._isConnected) {
            throw new Error('Not connected to backend server');
        }

        const message = {
            type: 'chat_connect',
            id: Utils.getUUID(),
            version: '1.0.0',
            timestamp: Date.now(),
            data: {
                credentials,
            },
        };

        await this._sendMessage(message);
        console.info(`ServerChatService: Connecting to Twitch chat as ${credentials.username}`);
        if (credentials.pinnedChannels && credentials.pinnedChannels.length > 0) {
            console.info(`ServerChatService: Will connect to ${credentials.pinnedChannels.length} pinned channels`);
        }
    }

    /**
     * Disconnect from Twitch chat
     */
    async disconnectFromChat(): Promise<void> {
        if (!this._isConnected) {
            return;
        }

        const message = {
            type: 'chat_disconnect',
            id: Utils.getUUID(),
            version: '1.0.0',
            timestamp: Date.now(),
        };

        await this._sendMessage(message);
        console.info('ServerChatService: Disconnecting from Twitch chat');
    }

    /**
     * Join a Twitch channel
     */
    async joinChannel(channelLogin: string, channelId?: string): Promise<void> {
        if (!this._isConnected) {
            throw new Error('Not connected to backend server');
        }

        const message = {
            type: 'chat_join_channel',
            id: Utils.getUUID(),
            version: '1.0.0',
            timestamp: Date.now(),
            data: {
                channelLogin: channelLogin.toLowerCase(),
                channelId,
            },
        };

        await this._sendMessage(message);
        this._connectedChannels.add(channelLogin.toLowerCase());
        console.info(`ServerChatService: Joining channel ${channelLogin}`);
    }

    /**
     * Leave a Twitch channel
     */
    async leaveChannel(channelLogin: string): Promise<void> {
        if (!this._isConnected) {
            return;
        }

        const message = {
            type: 'chat_leave_channel',
            id: Utils.getUUID(),
            version: '1.0.0',
            timestamp: Date.now(),
            data: {
                channelLogin: channelLogin.toLowerCase(),
            },
        };

        await this._sendMessage(message);
        this._connectedChannels.delete(channelLogin.toLowerCase());
        console.info(`ServerChatService: Leaving channel ${channelLogin}`);
    }

    /**
     * Send a message to a channel
     */
    async sendMessage(channelLogin: string, message: string, replyTo?: string): Promise<void> {
        if (!this._isConnected) {
            throw new Error('Not connected to backend server');
        }

        const wsMessage = {
            type: 'chat_send_message',
            id: Utils.getUUID(),
            version: '1.0.0',
            timestamp: Date.now(),
            data: {
                channelLogin: channelLogin.toLowerCase(),
                message,
                replyTo,
            },
        };

        await this._sendMessage(wsMessage);
        console.info(`ServerChatService: Sending message to ${channelLogin}: ${message}`);
    }

    /**
     * Request chat history
     */
    async requestChatHistory(options?: { channelId?: string; limit?: number; before?: number }): Promise<void> {
        if (!this._isConnected) {
            throw new Error('Not connected to backend server');
        }

        const message = {
            type: 'chat_history_request',
            id: Utils.getUUID(),
            version: '1.0.0',
            timestamp: Date.now(),
            data: options || {},
        };

        await this._sendMessage(message);
        console.info('ServerChatService: Requesting chat history');
    }

    /**
     * Get connection status
     */
    getConnectionStatus(): { isConnected: boolean; connectedChannels: string[] } {
        return {
            isConnected: this._isConnected,
            connectedChannels: Array.from(this._connectedChannels),
        };
    }

    /**
     * Setup WebSocket event handlers
     */
    private _setupWebSocketHandlers(): void {
        if (!this._websocket) return;

        this._websocket.onmessage = (event) => {
            try {
                const message = JSON.parse(event.data);
                this._handleWebSocketMessage(message);
            } catch (error) {
                console.error('ServerChatService: Failed to parse WebSocket message:', error);
            }
        };

        this._websocket.onclose = (event) => {
            this._isConnected = false;
            this._stopHeartbeat();
            console.info(`ServerChatService: WebSocket closed (${event.code}: ${event.reason})`);
            
            // Attempt reconnection unless it was a clean close
            if (event.code !== 1000) {
                this._handleReconnection();
            }
        };

        this._websocket.onerror = (error) => {
            console.error('ServerChatService: WebSocket error:', error);
        };
    }

    /**
     * Handle incoming WebSocket messages
     */
    private _handleWebSocketMessage(message: any): void {
        console.debug(`ServerChatService: Received message type: ${message.type}`);

        const handler = this._messageHandlers.get(message.type);
        if (handler) {
            handler(message.data);
        } else {
            console.warn(`ServerChatService: No handler for message type: ${message.type}`);
        }
    }

    /**
     * Setup message type handlers
     */
    private _setupMessageHandlers(): void {
        // Connection acknowledgment
        this._messageHandlers.set('connection_ack', (data: any) => {
            console.info('ServerChatService: Connection acknowledged by server');
            this.emit('connected');
        });

        // Heartbeat acknowledgment
        this._messageHandlers.set('heartbeat_ack', (data: any) => {
            // Heartbeat acknowledged, connection is healthy
        });

        // Chat message received
        this._messageHandlers.set('chat_message_received', (data: any) => {
            this._handleChatMessage(data);
        });

        // Chat state synchronization
        this._messageHandlers.set('chat_state_sync', (data: any) => {
            this._handleChatStateSync(data);
        });

        // Chat history response
        this._messageHandlers.set('chat_history_response', (data: any) => {
            this._handleChatHistory(data);
        });

        // Whisper message
        this._messageHandlers.set('chat_whisper', (data: any) => {
            this._handleWhisperMessage(data);
        });

        // User events
        this._messageHandlers.set('chat_user_join', (data: any) => {
            this.emit('userJoin', data);
        });

        this._messageHandlers.set('chat_user_part', (data: any) => {
            this.emit('userPart', data);
        });

        // Moderation events
        this._messageHandlers.set('chat_user_ban', (data: any) => {
            this.emit('userBan', data);
        });

        this._messageHandlers.set('chat_user_timeout', (data: any) => {
            this.emit('userTimeout', data);
        });

        this._messageHandlers.set('chat_message_deleted', (data: any) => {
            this.emit('messageDeleted', data);
        });

        this._messageHandlers.set('chat_clear_chat', (data: any) => {
            this.emit('clearChat', data);
        });

        // Subscription events
        this._messageHandlers.set('chat_subscription', (data: any) => {
            this.emit('subscription', data);
        });

        this._messageHandlers.set('chat_cheer', (data: any) => {
            this.emit('cheer', data);
        });

        this._messageHandlers.set('chat_raid', (data: any) => {
            this.emit('raid', data);
        });

        // Error handling
        this._messageHandlers.set('error', (data: any) => {
            console.error('ServerChatService: Server error:', data);
            this.emit('error', data);
        });
    }

    /**
     * Handle incoming chat messages
     */
    private _handleChatMessage(data: any): void {
        // Convert backend message format to frontend format
        const chatMessage: TwitchatDataTypes.MessageChatData = {
            id: data.id,
            date: data.timestamp,
            type: "message",
            platform: 'twitch',
            channel_id: data.channel_id,
            user: {
                id: data.user.id,
                login: data.user.login,
                displayNameOriginal: data.user.displayName,
                displayName: data.user.displayName,
                color: data.user.color,
                badges: data.user.badges || [],
                platform: 'twitch',
                channelInfo: {},
                is_partner: false,
                is_affiliate: false,
                is_tracked: false,
                is_bot: false,
                is_blocked: false,
                pronouns: null,
                pronounsLabel: false,
                pronounsTooltip: false,
            } as TwitchatDataTypes.TwitchatUser,
            message: data.message,
            message_chunks: this._parseMessageToChunks(data.message),
            message_html: data.message,
            message_size: data.message.length,
            answers: [],
            is_short: data.message.length < 50,
        };

        this.emit('message', chatMessage);
    }

    /**
     * Handle chat state synchronization
     */
    private _handleChatStateSync(data: any): void {
        console.info('ServerChatService: Received chat state sync');
        
        // Update connected channels
        this._connectedChannels.clear();
        data.connectedChannels?.forEach((channel: string) => {
            this._connectedChannels.add(channel);
        });

        // Process message history and emit individual message events
        if (data.messageHistory && Array.isArray(data.messageHistory)) {
            console.info(`ServerChatService: Restoring ${data.messageHistory.length} messages from history`);
            
            // Convert and emit each message individually so the chat interface displays them
            data.messageHistory.forEach((msg: any) => {
                try {
                    const chatMessage = this._convertBackendMessage(msg);
                    this.emit('message', chatMessage);
                } catch (error) {
                    console.error('ServerChatService: Failed to convert message from history:', error, msg);
                }
            });
        }

        // Emit state sync event for other components that need it
        this.emit('stateSync', {
            isConnected: data.isConnected,
            connectedChannels: data.connectedChannels,
            messageHistory: data.messageHistory,
        });
    }

    /**
     * Handle chat history response
     */
    private _handleChatHistory(data: any): void {
        console.info(`ServerChatService: Received ${data.messages.length} messages from history`);
        
        // Convert messages to frontend format and emit
        const messages = data.messages.map((msg: any) => this._convertBackendMessage(msg));
        
        this.emit('chatHistory', {
            messages,
            hasMore: data.hasMore,
            total: data.total,
        });
    }

    /**
     * Handle whisper messages
     */
    private _handleWhisperMessage(data: any): void {
        const whisperMessage: TwitchatDataTypes.MessageWhisperData = {
            id: Utils.getUUID(),
            date: data.timestamp,
            type: "whisper",
            platform: 'twitch',
            channel_id: "whispers",
            user: {
                id: data.user.id,
                login: data.user.login,
                displayNameOriginal: data.user.displayName,
                displayName: data.user.displayName,
                color: data.user.color,
                badges: data.user.badges || [],
                platform: 'twitch',
                channelInfo: {},
                is_partner: false,
                is_affiliate: false,
                is_tracked: false,
                is_bot: false,
                is_blocked: false,
                pronouns: null,
                pronounsLabel: false,
                pronounsTooltip: false,
            } as TwitchatDataTypes.TwitchatUser,
            to: {
                id: "self",
                login: "self",
                displayNameOriginal: "Self",
                displayName: "Self",
                platform: 'twitch',
                channelInfo: {},
                is_partner: false,
                is_affiliate: false,
                is_tracked: false,
                is_bot: false,
                is_blocked: false,
                pronouns: null,
                pronounsLabel: false,
                pronounsTooltip: false,
            } as TwitchatDataTypes.TwitchatUser,
            message: data.message,
            message_chunks: this._parseMessageToChunks(data.message),
            message_html: data.message,
            message_size: data.message.length,
        };

        this.emit('whisper', whisperMessage);
    }

    /**
     * Convert backend message to frontend format
     */
    private _convertBackendMessage(msg: any): TwitchatDataTypes.MessageChatData {
        return {
            id: msg.id,
            date: msg.timestamp,
            type: "message",
            platform: 'twitch',
            channel_id: msg.channel_id,
            user: {
                id: msg.user.id,
                login: msg.user.login,
                displayNameOriginal: msg.user.displayName,
                displayName: msg.user.displayName,
                color: msg.user.color,
                badges: msg.user.badges || [],
                platform: 'twitch',
                channelInfo: {},
                is_partner: false,
                is_affiliate: false,
                is_tracked: false,
                is_bot: false,
                is_blocked: false,
                pronouns: null,
                pronounsLabel: false,
                pronounsTooltip: false,
            } as TwitchatDataTypes.TwitchatUser,
            message: msg.message,
            message_chunks: this._parseMessageToChunks(msg.message),
            message_html: msg.message,
            message_size: msg.message.length,
            answers: [],
            is_short: msg.message.length < 50,
        };
    }

    /**
     * Parse message to chunks (simplified version)
     */
    private _parseMessageToChunks(message: string): TwitchatDataTypes.ParseMessageChunk[] {
        // Basic text chunk for now - would need full parsing logic for emotes/links
        return [{
            type: 'text',
            value: message,
        }];
    }

    /**
     * Send message through WebSocket
     */
    private async _sendMessage(message: any): Promise<void> {
        if (!this._websocket || this._websocket.readyState !== WebSocket.OPEN) {
            // Queue message for later if not connected
            this._messageQueue.push(message);
            return;
        }

        try {
            this._websocket.send(JSON.stringify(message));
        } catch (error) {
            console.error('ServerChatService: Failed to send message:', error);
            throw error;
        }
    }

    /**
     * Process queued messages
     */
    private _processMessageQueue(): void {
        while (this._messageQueue.length > 0) {
            const message = this._messageQueue.shift();
            this._sendMessage(message);
        }
    }

    /**
     * Start heartbeat to keep connection alive
     */
    private _startHeartbeat(): void {
        if (this._heartbeatInterval) {
            clearInterval(this._heartbeatInterval);
        }

        this._heartbeatInterval = window.setInterval(() => {
            if (this._isConnected && this._websocket) {
                const heartbeat = {
                    type: 'heartbeat',
                    id: Utils.getUUID(),
                    version: '1.0.0',
                    timestamp: Date.now(),
                };
                this._sendMessage(heartbeat);
            }
        }, 30000); // 30 seconds
    }

    /**
     * Stop heartbeat
     */
    private _stopHeartbeat(): void {
        if (this._heartbeatInterval) {
            clearInterval(this._heartbeatInterval);
            this._heartbeatInterval = null;
        }
    }

    /**
     * Handle reconnection logic
     */
    private async _handleReconnection(): Promise<void> {
        if (this._reconnectAttempts >= this._maxReconnectAttempts) {
            console.error('ServerChatService: Max reconnection attempts reached');
            this.emit('reconnectionFailed');
            return;
        }

        this._reconnectAttempts++;
        const delay = this._reconnectDelay * this._reconnectAttempts;

        console.info(`ServerChatService: Reconnecting in ${delay}ms (attempt ${this._reconnectAttempts}/${this._maxReconnectAttempts})`);

        setTimeout(async () => {
            try {
                await this.connect();
            } catch (error) {
                console.error('ServerChatService: Reconnection failed:', error);
                await this._handleReconnection();
            }
        }, delay);
    }

    /**
     * Get WebSocket URL based on current configuration
     */
    private _getWebSocketUrl(): string {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const host = window.location.host; // Use host instead of hostname to include port if specified
        const port = 8081; // Backend WebSocket port (server_port + 1)
        const path = '/ws';
        
        // If host already includes a port, replace it with the WebSocket port
        const hostWithoutPort = host.split(':')[0];
        
        const wsUrl = `${protocol}//${hostWithoutPort}:${port}${path}`;
        
        // Debug logging
        console.log('ServerChatService: WebSocket URL generation:', {
            protocol,
            host,
            hostWithoutPort,
            port,
            path,
            wsUrl
        });
        
        return wsUrl;
    }
}